import { useState, useEffect } from 'react';
import { Ticket, Plus, Pencil, Trash2, X, Award } from 'lucide-react';
import api from '@/utils/api';
import { formatGs } from '@/utils/helpers';
import { toast } from 'sonner';

const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

function CouponForm({ coupon, branches, onSave, onCancel }) {
  const [form, setForm] = useState({
    code: '', type: 'percentage', value: '', min_order_amount: '',
    branch_id: '', valid_from: '', valid_until: '', max_uses: '',
    single_use_per_user: true, active: true,
    ...(coupon || {}),
    value: coupon?.value || '',
    min_order_amount: coupon?.min_order_amount || '',
    max_uses: coupon?.max_uses || '',
    branch_id: coupon?.branch_id || '',
    valid_from: coupon?.valid_from ? coupon.valid_from.slice(0, 16) : '',
    valid_until: coupon?.valid_until ? coupon.valid_until.slice(0, 16) : '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        value: parseInt(form.value),
        min_order_amount: form.min_order_amount ? parseInt(form.min_order_amount) : 0,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        branch_id: form.branch_id ? parseInt(form.branch_id) : null,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
      };
      if (coupon) {
        await api.put(`/cupones/${coupon.id}`, payload);
        toast.success('Cupón actualizado');
      } else {
        await api.post('/cupones', payload);
        toast.success('Cupón creado');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar');
    }
    setSaving(false);
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800/50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-sm">{coupon ? 'Editar cupón' : 'Nuevo cupón'}</h3>
        <button onClick={onCancel} className="text-zinc-500 hover:text-white"><X size={16} /></button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Código</label>
            <input data-testid="coupon-code-input" value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} required className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm" placeholder="PRIMERA20" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Tipo</label>
            <select data-testid="coupon-type-select" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm">
              <option value="percentage">Porcentaje (%)</option>
              <option value="fixed">Monto fijo (Gs.)</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Valor {form.type === 'percentage' ? '(%)' : '(Gs.)'}</label>
            <input data-testid="coupon-value-input" type="number" value={form.value} onChange={e => setForm(p => ({ ...p, value: e.target.value }))} required className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Mínimo elegible (Gs.)</label>
            <input type="number" value={form.min_order_amount} onChange={e => setForm(p => ({ ...p, min_order_amount: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm" placeholder="0" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Sucursal (opcional)</label>
            <select value={form.branch_id} onChange={e => setForm(p => ({ ...p, branch_id: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm">
              <option value="">Todas</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Máx. usos total</label>
            <input type="number" value={form.max_uses} onChange={e => setForm(p => ({ ...p, max_uses: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm" placeholder="Ilimitado" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Válido desde</label>
            <input type="datetime-local" value={form.valid_from} onChange={e => setForm(p => ({ ...p, valid_from: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Válido hasta</label>
            <input type="datetime-local" value={form.valid_until} onChange={e => setForm(p => ({ ...p, valid_until: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.single_use_per_user} onChange={e => setForm(p => ({ ...p, single_use_per_user: e.target.checked }))} className="rounded" />
            <span className="text-zinc-300">1 uso por usuario</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={e => setForm(p => ({ ...p, active: e.target.checked }))} className="rounded" />
            <span className="text-zinc-300">Activo</span>
          </label>
        </div>
        <button data-testid="coupon-save-btn" type="submit" disabled={saving} className="w-full bg-[#E84C1C] text-white font-medium py-2.5 rounded-lg hover:bg-[#d43f12] transition-colors disabled:opacity-50 text-sm">
          {saving ? 'Guardando...' : (coupon ? 'Actualizar' : 'Crear cupón')}
        </button>
      </form>
    </div>
  );
}

export default function CouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loyaltyForm, setLoyaltyForm] = useState({ min_orders_month: 5, discount_percentage: 10, active: true, reward_valid_orders: 2 });

  const load = () => {
    api.get('/cupones').then(r => setCoupons(r.data.cupones)).catch(() => {});
    api.get('/cupones/fidelidad').then(r => { setLoyalty(r.data.fidelidad); setLoyaltyForm(r.data.fidelidad); }).catch(() => {});
    api.get('/sucursales').then(r => setBranches(r.data.sucursales || [])).catch(() => {});
  };
  useEffect(load, []);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este cupón?')) return;
    try {
      await api.delete(`/cupones/${id}`);
      toast.success('Cupón eliminado');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const saveLoyalty = async () => {
    try {
      await api.put('/cupones/fidelidad', loyaltyForm);
      toast.success('Fidelidad actualizada');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  return (
    <div data-testid="coupons-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Cupones y Fidelidad</h2>
          <p className="text-sm text-zinc-500">{coupons.length} cupones</p>
        </div>
        <button data-testid="coupon-new-btn" onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 bg-[#E84C1C] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#d43f12] transition-colors">
          <Plus size={16} /> Nuevo cupón
        </button>
      </div>

      {showForm && (
        <CouponForm
          coupon={editing}
          branches={branches}
          onSave={() => { setShowForm(false); setEditing(null); load(); }}
          onCancel={() => { setShowForm(false); setEditing(null); }}
        />
      )}

      {/* Coupons list */}
      <div className="space-y-2">
        {coupons.map(c => (
          <div key={c.id} data-testid={`coupon-row-${c.id}`} className="bg-zinc-900 border border-zinc-800/50 rounded-xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#E84C1C]/10 flex items-center justify-center shrink-0">
              <Ticket size={18} className="text-[#E84C1C]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-sm">{c.code}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${c.active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}>
                  {c.active ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <p className="text-xs text-zinc-400 mt-0.5">
                {c.type === 'percentage' ? `${c.value}%` : formatGs(c.value)}
                {c.branch_name ? ` · ${c.branch_name}` : ' · Todas las sucursales'}
                {c.min_order_amount > 0 ? ` · Mín. ${formatGs(c.min_order_amount)}` : ''}
                {c.max_uses ? ` · ${c.uses_count}/${c.max_uses} usos` : ` · ${c.uses_count} usos`}
              </p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => { setEditing(c); setShowForm(true); }} className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"><Pencil size={14} /></button>
              <button onClick={() => handleDelete(c.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {coupons.length === 0 && !showForm && (
          <p className="text-center text-sm text-zinc-500 py-8">No hay cupones creados.</p>
        )}
      </div>

      {/* Loyalty config — always visible for admin */}
      <div className="bg-zinc-900 border border-zinc-800/50 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Award size={18} className="text-amber-400" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Programa de Fidelidad</h3>
              <p className="text-xs text-zinc-500">Descuento automático por pedidos recurrentes</p>
            </div>
          </div>
          {loyalty && (
            <span data-testid="loyalty-status-badge" className={`text-[10px] px-2 py-1 rounded-full font-semibold ${loyaltyForm.active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
              {loyaltyForm.active ? 'Activo' : 'Desactivado'}
            </span>
          )}
        </div>
        {loyalty ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Pedidos mín./mes</label>
                <input data-testid="loyalty-min-orders" type="number" value={loyaltyForm.min_orders_month || ''} onChange={e => setLoyaltyForm(p => ({ ...p, min_orders_month: parseInt(e.target.value) || 0 }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Descuento (%)</label>
                <input data-testid="loyalty-percentage" type="number" value={loyaltyForm.discount_percentage || ''} onChange={e => setLoyaltyForm(p => ({ ...p, discount_percentage: parseInt(e.target.value) || 0 }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Usos del beneficio</label>
                <input data-testid="loyalty-valid-orders" type="number" min="1" value={loyaltyForm.reward_valid_orders || ''} onChange={e => setLoyaltyForm(p => ({ ...p, reward_valid_orders: parseInt(e.target.value) || 1 }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer mb-2">
                  <input data-testid="loyalty-active-toggle" type="checkbox" checked={!!loyaltyForm.active} onChange={e => setLoyaltyForm(p => ({ ...p, active: e.target.checked }))} className="rounded" />
                  <span className="text-zinc-300 text-xs">Activo</span>
                </label>
              </div>
            </div>
            <button data-testid="loyalty-save-btn" onClick={saveLoyalty} className="px-5 py-2 bg-amber-500 text-black text-xs font-semibold rounded-lg hover:bg-amber-400 transition-colors">
              Guardar
            </button>
          </div>
        ) : (
          <p className="text-xs text-zinc-500">Cargando configuración...</p>
        )}
      </div>
    </div>
  );
}
